import { router } from "@inertiajs/react"
import { CheckCircle2, Users } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { CandidateReviewCard } from "@/components/candidates/candidate-review-card"
import { EmptyState } from "@/components/empty-state"
import { HomeTriageSection } from "@/components/home-triage/home-triage-section"
import { useCurrentContext } from "@/hooks/use-current-context"
import type { CandidatesToReviewSection as SectionData } from "@/types/home-triage"

/**
 * OKS-708 — Agency "Candidates to review" section: the agency-home consumer
 * of the shipped {@link HomeTriageSection} shell + {@link CandidateReviewCard}.
 *
 * Structurally identical to the OKS-706 brand consumer, with three agency
 * deltas: (1) it reloads the AGENCY prop key `agency_candidates_to_review`
 * on Retry; (2) its cold-start / empty copy names the SELECTED brand
 * (OKS-854 — was phrased at the portfolio level, "across your brands", back
 * when home aggregated every assigned brand); (3) each card carries a
 * `brand` badge (supplied per item by the agency serializer — the card
 * renders it automatically).
 */
export interface AgencyCandidatesToReviewSectionProps {
  section: SectionData
}

export function AgencyCandidatesToReviewSection({
  section,
}: AgencyCandidatesToReviewSectionProps) {
  const { t } = useTranslation()
  const { currentBrand, availableBrands } = useCurrentContext()
  const [isRetrying, setIsRetrying] = useState(false)

  // OKS-854 — the copy names the selected brand (UX Spec §3). The
  // switcher hint only appears when there is somewhere to switch TO:
  // the switcher is hidden entirely for single-brand agencies, so
  // pointing at a control that isn't on screen would be worse than
  // saying nothing.
  const brandName = currentBrand?.name ?? ""
  const showSwitcherHint = availableBrands.length > 1

  function handleRetry() {
    setIsRetrying(true)
    router.reload({
      only: ["agency_candidates_to_review"],
      onFinish: () => setIsRetrying(false),
    })
  }

  const state = isRetrying ? "loading" : section.state

  return (
    <HomeTriageSection
      title={t("candidates.home_review.title")}
      state={state}
      count={section.count}
      viewAllUrl={section.view_all_url}
      onRetry={handleRetry}
      isRetrying={isRetrying}
      testId="agency-candidates-to-review"
      // OKS-953 (PRD R7 / UX Spec §5) — the agency home carries the SAME
      // five candidate segments, so a clean portfolio collapses to ~5 lines.
      // Collapsing only some of them would read as breakage in the others.
      collapseWhenEmpty
      emptyTestId="agency-candidates-to-review-empty"
      renderColdStart={() => (
        <EmptyState
          icon={Users}
          title={t("agency_home.candidates.cold_start.title")}
          titleAs="h2"
          description={t("agency_home.candidates.cold_start.description", {
            brand: brandName,
          })}
          data-testid="agency-candidates-to-review-cold-start"
        />
      )}
      renderEmpty={() => (
        <EmptyState
          icon={CheckCircle2}
          title={t("agency_home.candidates.empty.title")}
          titleAs="h2"
          description={[
            t("agency_home.candidates.empty.description", { brand: brandName }),
            showSwitcherHint
              ? t("agency_home.candidates.empty.switcher_hint")
              : null,
          ]
            .filter(Boolean)
            .join(" ")}
          data-testid="agency-candidates-to-review-empty"
        />
      )}
    >
      {section.items.map((item) => (
        <CandidateReviewCard key={item.id} item={item} />
      ))}
    </HomeTriageSection>
  )
}
